import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  WalletBatchItemsFilters,
  WalletBatchItemsPage,
  WalletNavigation,
  WalletReportConfig,
  WalletReportDownloadUrl,
  WalletReportExport,
  WalletReportExportsPage,
  WalletReportPreview,
  WalletReportTemplate,
  WalletReportType,
  WalletReportsCatalog,
} from 'src/app/models/WalletReports';
import { WalletReportsMockService } from './wallet-reports-mock.service';

/**
 * Interruptores do M7 (task 17tm466nz8b), UM POR ENDPOINT — porque o m7-back foi publicando
 * em levas (`~/trabalhos/m7-coord.md`, seção "Status de implementação", 14/10) em vez de tudo
 * de uma vez. Cada chave vira `false` quando o endpoint correspondente responder de verdade
 * na branch; nenhum componente conhece mock ou HTTP, todos chamam só `WalletReportsService`.
 *
 * Estado em 17/10 (correção do QA do João, achados M7.4/M7.5/M7.6): todos os tipos
 * (`entries`, `consumption`, `dispatches` e `recharges` — a ambiguidade do tipo legado do
 * PLANO.md §3b foi resolvida) já estão prontos e testados no back — ligados. Lote expande
 * 1 linha por mensagem, rateio exato; telefone vem cru, SEM máscara — diferente do extrato,
 * decisão do back para o relatório. Exportação em `.xlsx` agora tem writer no back
 * (openspout) — sai do mock nos dois formatos. Modelos salvos (CRUD completo) também prontos.
 */
export const WALLET_REPORTS_USE_MOCK = {
  navigation: false,
  batchItems: false,
  catalog: false,
  previewByType: {
    entries: false,
    consumption: false,
    dispatches: false,
    recharges: false,
  } as Record<WalletReportType, boolean>,
  exports: false,
  templates: false,
};

/**
 * Fachada única dos endpoints novos da carteira (navegação do menu, painel do lote,
 * catálogo/prévia/export/histórico/modelos de relatório). Decide mock × HTTP real pelas
 * chaves de `WALLET_REPORTS_USE_MOCK` — trocar é mudar essa constante, nada nos componentes.
 */
@Injectable({ providedIn: 'root' })
export class WalletReportsService {

  constructor(private http: HttpClient, private mock: WalletReportsMockService) {}

  /**
   * O cliente não pode confundir dado de demonstração com o relatório de verdade (achado de
   * QA, 14/10): a tela usa isto para avisar "amostra" na prévia e desabilitar o que ainda não
   * existe de fato (exportar, modelos). Mesma leitura que `preview()` faz internamente.
   */
  isPreviewMocked(type: WalletReportType): boolean {
    return WALLET_REPORTS_USE_MOCK.previewByType[type];
  }

  get isExportMocked(): boolean {
    return WALLET_REPORTS_USE_MOCK.exports;
  }

  get isTemplatesMocked(): boolean {
    return WALLET_REPORTS_USE_MOCK.templates;
  }

  navigation(): Observable<WalletNavigation> {
    if (WALLET_REPORTS_USE_MOCK.navigation) { return this.mock.navigation(); }
    return this.http.get<WalletNavigation>(`${environment.apiUrl}/wallet/navigation`);
  }

  catalog(): Observable<WalletReportsCatalog> {
    if (WALLET_REPORTS_USE_MOCK.catalog) { return this.mock.catalog(); }
    return this.http.get<WalletReportsCatalog>(`${environment.apiUrl}/wallet/reports/catalog`);
  }

  batchItems(entryId: string, filters: WalletBatchItemsFilters): Observable<WalletBatchItemsPage> {
    if (WALLET_REPORTS_USE_MOCK.batchItems) { return this.mock.batchItems(entryId, filters); }

    let params = new HttpParams().set('at', filters.at);
    if (filters.page) { params = params.set('page', String(filters.page)); }
    if (filters.per_page) { params = params.set('per_page', String(filters.per_page)); }
    if (filters.q) { params = params.set('q', filters.q); }

    return this.http.get<WalletBatchItemsPage>(`${environment.apiUrl}/wallet/entries/${entryId}/items`, { params });
  }

  /** `entries`/`consumption`/`dispatches` já têm prévia real; só `recharges` ainda é 501 no back. */
  preview(config: WalletReportConfig): Observable<WalletReportPreview> {
    if (WALLET_REPORTS_USE_MOCK.previewByType[config.type]) { return this.mock.preview(config); }
    return this.http.post<WalletReportPreview>(`${environment.apiUrl}/wallet/reports/preview`, toRequestBody(config));
  }

  createExport(config: WalletReportConfig): Observable<WalletReportExport> {
    if (WALLET_REPORTS_USE_MOCK.exports) { return this.mock.createExport(config); }
    return unwrap(this.http.post<{ data: WalletReportExport }>(`${environment.apiUrl}/wallet/reports/exports`, toRequestBody(config)));
  }

  listExports(page = 1, perPage = 20): Observable<WalletReportExportsPage> {
    if (WALLET_REPORTS_USE_MOCK.exports) { return this.mock.listExports(); }

    const params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    return this.http.get<WalletReportExportsPage>(`${environment.apiUrl}/wallet/reports/exports`, { params });
  }

  getExport(id: string): Observable<WalletReportExport | null> {
    if (WALLET_REPORTS_USE_MOCK.exports) { return this.mock.getExport(id); }
    return unwrap(this.http.get<{ data: WalletReportExport }>(`${environment.apiUrl}/wallet/reports/exports/${id}`));
  }

  downloadUrl(id: string): Observable<WalletReportDownloadUrl> {
    if (WALLET_REPORTS_USE_MOCK.exports) { return this.mock.downloadUrl(id); }
    return this.http.post<WalletReportDownloadUrl>(`${environment.apiUrl}/wallet/reports/exports/${id}/download-url`, {});
  }

  deleteExport(id: string): Observable<void> {
    if (WALLET_REPORTS_USE_MOCK.exports) { return this.mock.deleteExport(id); }
    return this.http.delete<void>(`${environment.apiUrl}/wallet/reports/exports/${id}`);
  }

  listTemplates(type?: WalletReportType): Observable<{ data: WalletReportTemplate[] }> {
    if (WALLET_REPORTS_USE_MOCK.templates) { return this.mock.listTemplates(type); }

    let params = new HttpParams();
    if (type) { params = params.set('type', type); }
    return this.http.get<{ data: WalletReportTemplate[] }>(`${environment.apiUrl}/wallet/reports/templates`, { params });
  }

  createTemplate(name: string, type: WalletReportType, config: WalletReportTemplate['config']): Observable<WalletReportTemplate> {
    if (WALLET_REPORTS_USE_MOCK.templates) { return this.mock.createTemplate(name, type, config); }
    return unwrap(this.http.post<{ data: WalletReportTemplate }>(`${environment.apiUrl}/wallet/reports/templates`, { name, type, config }));
  }

  deleteTemplate(id: string): Observable<void> {
    if (WALLET_REPORTS_USE_MOCK.templates) { return this.mock.deleteTemplate(id); }
    return this.http.delete<void>(`${environment.apiUrl}/wallet/reports/templates/${id}`);
  }
}

/** Corpo comum de `preview`/`exports`: tipo, filtros, colunas (na ordem escolhida) e formato. */
function toRequestBody(config: WalletReportConfig): {
  type: WalletReportType;
  filters: WalletReportConfig['filters'];
  columns: Array<{ key: string; label: string; visible: boolean }>;
  format: WalletReportConfig['format'];
} {
  return { type: config.type, filters: config.filters, columns: config.columns, format: config.format };
}

function unwrap<T>(source: Observable<{ data: T }>): Observable<T> {
  return source.pipe(map((response) => response.data));
}
